import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import {
    DefaultButton,
    Dialog,
    Dropdown,
    DialogFooter,
    DialogType,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    TooltipHost,
    useTheme
} from '@fluentui/react';
import { toast } from 'react-toastify';
import PanelWrapper from '../Common/PanelWrapper';
import { useGetOrganizationEntitlementsQuery } from '../../store/endpoints/OrganizationsEndpoints';
import {
    useAddCustomDomainMutation,
    useDeleteCustomDomainMutation,
    useGetCustomDomainsQuery,
    useGetStorefrontsQuery,
    usePatchCustomDomainMutation,
    useRefreshCustomDomainMutation
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import { ICustomDomain, ICustomDomainDnsRecord } from '../../models/CustomDomains';

/* How often to re-check pending domains against Cloudflare while this section is mounted. Each tick
 * hits the refresh endpoint (which advances verification server-side), not just the list. */
const PENDING_POLL_MS = 15000;

const RECORD_PURPOSE_HELP: Record<string, string> = {
    routing: 'Points your domain at your storefront. You can add this last — the storefront keeps working on its standard URL until everything is verified.',
    ownership: 'Proves you control the domain.',
    certificate: 'Lets us issue the HTTPS certificate.',
};

const CopyValue: React.FC<{ value?: string }> = ({ value }) => {
    const [copied, setCopied] = React.useState(false);
    if (!value) return null;
    return (
        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
            <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{value}</code>
            <TooltipHost content='Copy to clipboard'>
                <IconButton
                    iconProps={{ iconName: copied ? 'CheckMark' : 'Copy' }}
                    ariaLabel='Copy to clipboard'
                    onClick={() => {
                        navigator.clipboard?.writeText(value);
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 1500);
                    }}
                />
            </TooltipHost>
        </Stack>
    );
};

/* The registrable (apex) domain of a hostname — the zone a DNS provider's Host field auto-appends.
 * Usually the last two labels, but multi-label public suffixes (co.uk, com.au, ...) span three: a
 * short generic second-to-last label under a two-letter ccTLD is the tell. Not a full public-suffix
 * list, and only a copy-helper hint rides on it — the full record name is always shown as primary. */
const MULTI_LABEL_SUFFIX_SLDS = ['co', 'com', 'org', 'net', 'gov', 'edu', 'ac'];
const apexDomainOf = (hostname?: string): string | undefined => {
    if (!hostname) return undefined;
    const labels = hostname.split('.');
    if (labels.length < 2) return undefined;
    const tld = labels[labels.length - 1];
    const sld = labels[labels.length - 2];
    const take = labels.length > 2 && tld.length === 2 && MULTI_LABEL_SUFFIX_SLDS.includes(sld) ? 3 : 2;
    return labels.slice(-take).join('.');
};

/* The record name relative to the registrable domain — what providers whose Host field auto-appends
 * the domain (Namecheap, GoDaddy, ...) actually need. "@" for the apex itself. */
const relativeRecordName = (name?: string, apexDomain?: string): string | undefined => {
    if (!name || !apexDomain) return undefined;
    if (name === apexDomain) return '@';
    if (name.endsWith(`.${apexDomain}`)) return name.slice(0, -(apexDomain.length + 1));
    return undefined;
};

const DnsRecordRow: React.FC<{ record: ICustomDomainDnsRecord, apexDomain?: string }> = ({ record, apexDomain }) => {
    const theme = useTheme();
    const relativeName = relativeRecordName(record.name, apexDomain);
    return (
        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign='start' styles={{ root: { padding: '4px 0' } }}>
            <Stack.Item styles={{ root: { width: 60 } }}>
                <Text variant='small' styles={{ root: { fontWeight: 600 } }}>{record.type}</Text>
            </Stack.Item>
            <Stack.Item grow styles={{ root: { minWidth: 0, flexBasis: '40%' } }}>
                <Text variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>Name</Text>
                <CopyValue value={record.name} />
                {relativeName && (
                    <Stack tokens={{ childrenGap: 2 }} styles={{ root: { marginTop: 4 } }}>
                        <Text variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            If your DNS provider appends {apexDomain} automatically, enter only:
                        </Text>
                        <CopyValue value={relativeName} />
                    </Stack>
                )}
            </Stack.Item>
            <Stack.Item grow styles={{ root: { minWidth: 0, flexBasis: '40%' } }}>
                <Text variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>Value</Text>
                <CopyValue value={record.value} />
            </Stack.Item>
        </Stack>
    );
};

const DomainStatus: React.FC<{ domain: ICustomDomain }> = ({ domain }) => {
    const theme = useTheme();
    if (domain.status === 'active') {
        return <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 6 }}>
            <IconButton iconProps={{ iconName: 'SkypeCircleCheck' }} styles={{ root: { color: theme.palette.green, pointerEvents: 'none' }, icon: { color: theme.palette.green } }} />
            <Text>Verified — available in the Storefront Domain dropdown</Text>
        </Stack>;
    }
    if (domain.status === 'failed') {
        return <MessageBar messageBarType={MessageBarType.error}>{domain.failureReason ?? 'Verification failed.'}</MessageBar>;
    }
    return <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
        <Spinner size={SpinnerSize.small} />
        <Text>
            Waiting for DNS records
            {domain.ownershipStatus === 'active' ? ' — ownership verified, issuing certificate' : ''}
        </Text>
    </Stack>;
};

/**
 * Self-serve custom domain management (AC-1072). Org-level: domains added here are shared by all of the
 * organization's storefronts once verified (they appear in the Storefront Domain dropdown above).
 * Rendered only when the org has the customDomainsEnabled entitlement.
 */
const CustomDomainsSection: React.FC = () => {
    const theme = useTheme();
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const entitlementsQuery = useGetOrganizationEntitlementsQuery(organization ?? '', { skip: !organization || organization === '' });
    const enabled = entitlementsQuery.data?.customDomainsEnabled === true;

    const domainsQuery = useGetCustomDomainsQuery(undefined, { skip: !enabled });
    const storefrontsQuery = useGetStorefrontsQuery(undefined, { skip: !enabled });
    const [addDomain, addResult] = useAddCustomDomainMutation();
    const [patchDomain] = usePatchCustomDomainMutation();
    const [refreshDomain] = useRefreshCustomDomainMutation();
    const [deleteDomain, deleteResult] = useDeleteCustomDomainMutation();

    const [newHostname, setNewHostname] = React.useState('');
    const [message, setMessage] = React.useState<string | undefined>();
    const [addOpen, setAddOpen] = React.useState(false);
    const [addMessage, setAddMessage] = React.useState<string | undefined>();
    const [detailsDomainId, setDetailsDomainId] = React.useState<string | undefined>();
    // Scoped to a domain id so a check in flight for one domain never paints another domain's panel.
    const [checkState, setCheckState] = React.useState<{ id: string, status: 'checking' | 'checked' } | undefined>();
    const [pendingDelete, setPendingDelete] = React.useState<ICustomDomain | undefined>();

    const domains = React.useMemo(() => domainsQuery.data?.items ?? [], [domainsQuery.data]);
    const detailsDomain = React.useMemo(
        () => domains.find(d => d.id === detailsDomainId),
        [domains, detailsDomainId]);
    const storefronts = React.useMemo(() => storefrontsQuery.data?.items ?? [], [storefrontsQuery.data]);
    const storefrontName = React.useCallback(
        (id?: string) => storefronts.find(s => s.id === id)?.name ?? id,
        [storefronts]);
    // Keyed by contents rather than array identity: every refetch rebuilds the domains array, and an
    // identity-keyed effect would tear down and restart the interval on each poll.
    const pendingKey = domains
        .filter(d => d.status === 'pending' && d.id)
        .map(d => d.id as string)
        .join(',');

    // Re-check pending domains on an interval; the refresh mutation invalidates the list tag, so the
    // rows below update on their own when Cloudflare reports progress.
    React.useEffect(() => {
        if (pendingKey === '') return;
        const ids = pendingKey.split(',');
        const interval = window.setInterval(() => {
            ids.forEach(id => refreshDomain(id));
        }, PENDING_POLL_MS);
        return () => window.clearInterval(interval);
    }, [pendingKey, refreshDomain]);

    if (!enabled) return null;

    const limit = entitlementsQuery.data?.customDomainsLimit ?? 1;
    const activeCount = domains.filter(d => d.status !== 'failed').length;
    const atLimit = activeCount >= limit;

    const handleAdd = async () => {
        setAddMessage(undefined);
        const hostname = newHostname.trim();
        const toastId = toast.loading(`Adding ${hostname}…`);
        try {
            const result = await addDomain({ hostname }).unwrap();
            if (result.success) {
                toast.update(toastId, { type: 'success', render: `${hostname} added — DNS records ready`, isLoading: false, autoClose: 3000 });
                setNewHostname('');
                setAddOpen(false);
                // Land the user straight on the records they need to copy.
                if (result.id) setDetailsDomainId(result.id);
            } else {
                toast.update(toastId, { type: 'error', render: `Failed to add ${hostname}`, isLoading: false, autoClose: 4000 });
                setAddMessage(result.message ?? 'The domain could not be added.');
            }
        } catch {
            toast.update(toastId, { type: 'error', render: `Failed to add ${hostname}`, isLoading: false, autoClose: 4000 });
            setAddMessage('The domain could not be added. Please try again.');
        }
    };

    const handleCheckNow = async (domain: ICustomDomain) => {
        if (!domain.id) return;
        const domainId = domain.id;
        setCheckState({ id: domainId, status: 'checking' });
        try {
            await refreshDomain(domainId).unwrap();
            setCheckState({ id: domainId, status: 'checked' });
            window.setTimeout(
                () => setCheckState(s => s?.id === domainId && s.status === 'checked' ? undefined : s), 3000);
        } catch {
            toast.error(`Could not check ${domain.hostname}. Please try again.`);
            setCheckState(s => s?.id === domainId ? undefined : s);
        }
    };

    const openAddPanel = () => {
        setAddMessage(undefined);
        setNewHostname('');
        setAddOpen(true);
    };

    const handleDelete = async () => {
        if (!pendingDelete?.id) return;
        const hostname = pendingDelete.hostname;
        setMessage(undefined);
        setPendingDelete(undefined);
        const toastId = toast.loading(`Removing ${hostname}…`);
        try {
            const result = await deleteDomain(pendingDelete.id).unwrap();
            if (result.success) {
                toast.update(toastId, { type: 'success', render: `${hostname} removed`, isLoading: false, autoClose: 3000 });
            } else {
                toast.update(toastId, { type: 'error', render: `Failed to remove ${hostname}`, isLoading: false, autoClose: 4000 });
                setMessage(result.message ?? 'The domain could not be removed.');
            }
        } catch {
            toast.update(toastId, { type: 'error', render: `Failed to remove ${hostname}`, isLoading: false, autoClose: 4000 });
            setMessage('The domain could not be removed. Please try again.');
        }
    };

    const handleSetDefaultStorefront = async (domainId: string, storefrontId: string) => {
        const toastId = toast.loading('Saving default storefront…');
        try {
            const result = await patchDomain({ id: domainId, defaultStorefront: storefrontId }).unwrap();
            if (result.success) {
                toast.update(toastId, { type: 'success', render: 'Default storefront saved', isLoading: false, autoClose: 3000 });
            } else {
                toast.update(toastId, { type: 'error', render: result.message ?? 'Failed to save default storefront', isLoading: false, autoClose: 4000 });
            }
        } catch {
            toast.update(toastId, { type: 'error', render: 'Failed to save default storefront', isLoading: false, autoClose: 4000 });
        }
    };

    return (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
                <Label>Custom Domains</Label>
                <TooltipHost content={atLimit ? 'Domain limit reached' : 'Add a domain'}>
                    <IconButton
                        iconProps={{ iconName: 'Add' }}
                        ariaLabel='Add a domain'
                        disabled={atLimit}
                        onClick={openAddPanel}
                    />
                </TooltipHost>
            </Stack>
            <Text variant='small'>
                Verified domains are shared by all of your organization's storefronts.
            </Text>
            <Text variant='small' styles={{ root: { fontWeight: 600 } }}>
                {activeCount} of {limit} in use
            </Text>
            {message && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setMessage(undefined)}>
                    {message}
                </MessageBar>
            )}
            {domains.map(domain => (
                <Stack key={domain.id} tokens={{ childrenGap: '0.5em' }}
                       styles={{ root: { border: `1px solid ${theme.palette.neutralQuaternaryAlt}`, borderRadius: 4, padding: '0.75em' } }}>
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                        <Text variant='mediumPlus' styles={{ root: { fontWeight: 600 } }}>{domain.hostname}</Text>
                        <Stack horizontal tokens={{ childrenGap: 4 }}>
                            <TooltipHost content={domain.status === 'pending' ? 'View DNS records' : 'Domain settings'}>
                                <IconButton iconProps={{ iconName: domain.status === 'pending' ? 'View' : 'Settings' }}
                                            ariaLabel='Domain details'
                                            onClick={() => setDetailsDomainId(domain.id)} />
                            </TooltipHost>
                            <TooltipHost content='Remove domain'>
                                <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Remove domain'
                                            disabled={deleteResult.isLoading}
                                            onClick={() => setPendingDelete(domain)} />
                            </TooltipHost>
                        </Stack>
                    </Stack>
                    <DomainStatus domain={domain} />
                    {domain.status === 'active' && (
                        <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            {domain.defaultStorefront
                                ? `Default storefront: ${storefrontName(domain.defaultStorefront)}`
                                : 'No default storefront set — the bare domain shows the generic sign-in page.'}
                        </Text>
                    )}
                </Stack>
            ))}
            <PanelWrapper
                isOpen={addOpen}
                onDismiss={() => setAddOpen(false)}
                title='Add a Custom Domain'
                type={PanelType.smallFixedFar}
                isFooterAtBottom={true}
                footerButtons={[
                    <PrimaryButton
                        key='add'
                        text='Add Domain'
                        disabled={addResult.isLoading || newHostname.trim() === ''}
                        onClick={handleAdd}
                    />,
                    <DefaultButton key='cancel' text='Cancel' onClick={() => setAddOpen(false)} />
                ]}>
                <Stack tokens={{ childrenGap: '1em' }}>
                    <Text>
                        Enter a subdomain you own, like store.yourcompany.com. You'll get the DNS records
                        to add at your DNS provider; verification and the HTTPS certificate are handled
                        automatically. Your storefront keeps working on its standard URL until the domain
                        is verified.
                    </Text>
                    {addMessage && (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setAddMessage(undefined)}>
                            {addMessage}
                        </MessageBar>
                    )}
                    <TextField
                        label='Domain'
                        placeholder='store.yourcompany.com'
                        value={newHostname}
                        disabled={addResult.isLoading}
                        onChange={(e, v) => setNewHostname(v ?? '')}
                        onKeyDown={(e) => { if (e.key === 'Enter' && newHostname.trim() !== '') handleAdd(); }}
                    />
                </Stack>
            </PanelWrapper>
            <PanelWrapper
                isOpen={!!detailsDomain}
                onDismiss={() => setDetailsDomainId(undefined)}
                title={detailsDomain?.hostname ?? 'DNS Records'}
                type={PanelType.medium}>
                {detailsDomain && (
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <DomainStatus domain={detailsDomain} />
                        {detailsDomain.lastCheckedAt && (
                            <Text variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                Last checked {new Date(detailsDomain.lastCheckedAt).toLocaleTimeString()}
                            </Text>
                        )}
                        <Dropdown
                            label='Default Storefront'
                            selectedKey={detailsDomain.defaultStorefront ?? ''}
                            options={[
                                { key: '', text: 'None' },
                                ...storefronts
                                    .filter(s => s.id)
                                    .map(s => ({ key: s.id as string, text: s.name ?? (s.id as string) })),
                            ]}
                            onChange={(e, item) => {
                                if (!detailsDomain.id || item === undefined) return;
                                handleSetDefaultStorefront(detailsDomain.id, (item.key as string) ?? '');
                            }}
                        />
                        <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            Visitors who open {detailsDomain.hostname} with no path land in the default
                            storefront. With no default, the bare domain shows the generic sign-in page.
                        </Text>
                        <Text>
                            Add these records at your DNS provider. Verification runs automatically and
                            usually completes within minutes of the records propagating; your storefront
                            keeps working on its standard URL in the meantime.
                        </Text>
                        <Stack tokens={{ childrenGap: '0.75em' }}>
                            {detailsDomain.dnsRecords?.map((record, i) => (
                                <Stack key={`${record.purpose}-${i}`}
                                       styles={{ root: { border: `1px solid ${theme.palette.neutralQuaternaryAlt}`, borderRadius: 4, padding: '0.5em 0.75em' } }}>
                                    <DnsRecordRow record={record}
                                                  apexDomain={apexDomainOf(detailsDomain.hostname)} />
                                    {record.purpose && RECORD_PURPOSE_HELP[record.purpose] && (
                                        <Text variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                            {RECORD_PURPOSE_HELP[record.purpose]}
                                        </Text>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                        {detailsDomain.status === 'pending' && detailsDomain.id && (() => {
                            const checking = checkState?.id === detailsDomain.id && checkState.status === 'checking';
                            const justChecked = checkState?.id === detailsDomain.id && checkState.status === 'checked';
                            return (
                                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                                    <DefaultButton
                                        text={checking ? 'Checking…' : justChecked ? 'Checked' : 'Check verification now'}
                                        iconProps={checking ? undefined : { iconName: justChecked ? 'CheckMark' : 'Refresh' }}
                                        disabled={checking}
                                        onClick={() => handleCheckNow(detailsDomain)}
                                    />
                                    {checking && <Spinner size={SpinnerSize.small} />}
                                </Stack>
                            );
                        })()}
                    </Stack>
                )}
            </PanelWrapper>
            <Dialog
                hidden={!pendingDelete}
                onDismiss={() => setPendingDelete(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Remove custom domain',
                    subText: `${pendingDelete?.hostname} will stop routing to your storefronts. Storefronts using it fall back to their standard URL.`
                }}>
                <DialogFooter>
                    <PrimaryButton text='Remove' onClick={handleDelete} />
                    <DefaultButton text='Cancel' onClick={() => setPendingDelete(undefined)} />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
};

export default CustomDomainsSection;
